/* eslint-disable no-console */
import axios, {
  AxiosError,
  AxiosHeaderValue,
  AxiosInstance,
  AxiosRequestConfig,
  HeadersDefaults,
  InternalAxiosRequestConfig,
} from "axios";
import jwt_decode from "jwt-decode";

import LOCAL_STORAGE from "@/constants/local-storage";
import { logSessionEvent } from "@/utils/session-log";
import ROUTE_URLS from "@/constants/route-urls";
import { isImpersonationSession } from "@/helpers/auth-helpers";
import { IDecodedToken } from "@/interfaces/auth.interfaces";
import { isTokenExpired } from "@/utils/token-utils";

export type TRequestData = Record<never, unknown>;

export type TRequestHeaders = HeadersDefaults & {
  [key: string]: AxiosHeaderValue;
};

let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value?: unknown) => void;
  reject: (error?: unknown) => void;
}> = [];

const processQueue = (error: AxiosError | null, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });

  failedQueue = [];
};

const forceLogout = (reason = "desconhecido") => {
  logSessionEvent(`forceLogout (http-client): ${reason}`);
  localStorage.removeItem(LOCAL_STORAGE.accessTokenKey);
  localStorage.removeItem(LOCAL_STORAGE.refreshTokenKey);
  window.dispatchEvent(new CustomEvent("tokenExpired"));

  const currentPath = window.location.pathname;
  const isPublicRoute =
    currentPath.includes("/login") ||
    currentPath.includes("/register") ||
    currentPath.includes("/signup") ||
    currentPath.includes("/terms") ||
    currentPath.includes("/privacy");

  if (!isPublicRoute) {
    window.location.href = "/login";
  }
};

export class HttpClient {
  private axiosInstance: AxiosInstance;

  constructor(private baseUrl: string) {
    this.axiosInstance = axios.create({ baseURL: baseUrl });
    this.setupInterceptors();
  }

  private async refreshAccessToken(refreshToken: string): Promise<string | null> {
    try {
      const response = await axios.post(`${this.baseUrl}/api/v1/login/refreshtoken`, {
        refreshToken,
      });

      const { refreshToken: newRefreshToken, accessToken: newAccessToken } = response.data;

      if (!newRefreshToken || !newAccessToken) {
        return null;
      }

      localStorage.setItem(LOCAL_STORAGE.accessTokenKey, newAccessToken);
      localStorage.setItem(LOCAL_STORAGE.refreshTokenKey, newRefreshToken);

      return newAccessToken;
    } catch (error) {
      console.error("Error refreshing access token:", error);
      return null;
    }
  }

  private setupInterceptors() {
    this.axiosInstance.interceptors.request.use(async (config) => {
      const isAuthRoute = config.url?.includes("/login") || config.url?.includes("/register");
      const isInternalLogin = config.data?.login && config.data?.password;

      if (isAuthRoute && !isInternalLogin) {
        return config;
      }

      let accessToken = localStorage.getItem(LOCAL_STORAGE.accessTokenKey);
      const refreshToken = localStorage.getItem(LOCAL_STORAGE.refreshTokenKey);

      if (!accessToken) {
        return config;
      }

      try {
        let decodedToken: IDecodedToken = jwt_decode(accessToken);

        if (isTokenExpired(decodedToken) && refreshToken) {
          const newAccessToken = await this.refreshAccessToken(refreshToken);
          if (newAccessToken) {
            accessToken = newAccessToken;
            decodedToken = jwt_decode(newAccessToken);
          }
        }

        if (!isTokenExpired(decodedToken) && accessToken) {
          config.headers.Authorization = `Bearer ${accessToken}`;
          config.headers.CustomerId = decodedToken.CustomerId || "";
        }
      } catch (error) {
        console.error("Error decoding token:", error);
      }

      return config;
    });

    this.axiosInstance.interceptors.response.use(
      (response) => response,
      async (error: AxiosError) => {
        const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

        if (!error.response || error.response.status !== 401 || originalRequest._retry) {
          return Promise.reject(error);
        }

        if (originalRequest.url?.includes("/refreshtoken")) {
          forceLogout("ponto 1");
          return Promise.reject(error);
        }

        // Sessão de impersonation não tem refresh token. 401 de permissão (legado) não deve deslogar
        // se o JWT ainda for válido — autenticação ≠ autorização.
        try {
          const accessToken = localStorage.getItem(LOCAL_STORAGE.accessTokenKey);
          if (accessToken) {
            const decodedToken: IDecodedToken = jwt_decode(accessToken);
            if (isImpersonationSession(decodedToken)) {
              if (isTokenExpired(decodedToken)) {
                forceLogout("ponto 2");
              }
              return Promise.reject(error);
            }
          }
        } catch {
          // segue para fluxo de refresh / logout
        }

        if (isRefreshing) {
          return new Promise((resolve, reject) => {
            failedQueue.push({ resolve, reject });
          })
            .then((token) => {
              if (originalRequest.headers) {
                originalRequest.headers.Authorization = `Bearer ${token}`;
              }
              return this.axiosInstance(originalRequest);
            })
            .catch((err) => Promise.reject(err));
        }

        originalRequest._retry = true;
        isRefreshing = true;

        const refreshToken = localStorage.getItem(LOCAL_STORAGE.refreshTokenKey);

        if (!refreshToken) {
          processQueue(error, null);
          isRefreshing = false;
          forceLogout("ponto 3");
          return Promise.reject(error);
        }

        const newAccessToken = await this.refreshAccessToken(refreshToken);

        if (!newAccessToken) {
          processQueue(error, null);
          isRefreshing = false;
          forceLogout("ponto 4");
          return Promise.reject(error);
        }

        processQueue(null, newAccessToken);
        isRefreshing = false;

        if (originalRequest.headers) {
          originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
        }

        return this.axiosInstance(originalRequest);
      }
    );
  }

  public buildHeaders(_accessToken?: string) {
    let accessToken: string | null | undefined = _accessToken;
    if (!_accessToken) {
      accessToken = localStorage ? localStorage.getItem(LOCAL_STORAGE.accessTokenKey) : "";
    }

    let customerId: string | null = null;
    if (accessToken) {
      try {
        const decodedToken: IDecodedToken = jwt_decode(accessToken);
        customerId = decodedToken.CustomerId;
      } catch (error) {
        console.error("Error decoding token:", error);
      }
    }

    const headers = {
      Authorization: `Bearer ${accessToken}`,
      Accept: "application/json",
      "Content-Type": "application/json",
      CustomerId: customerId || "",
    };

    return headers;
  }

  async request<T = never, R = TRequestData>(
    method: string,
    path: string,
    body?: R,
    config?: AxiosRequestConfig
  ): Promise<T> {
    const headers = this.buildHeaders();

    const response = await this.axiosInstance.request<T>({
      method,
      url: path,
      data: body,
      headers,
      ...config,
    });

    return response.data;
  }

  async post<T = never>(
    path: string,
    body?: TRequestData,
    config?: AxiosRequestConfig
  ): Promise<T> {
    const headers = this.buildHeaders();
    return this.request<T>("post", path, body, {
      ...config,
      headers: { ...headers, ...config?.headers },
    });
  }

  async put<T = never>(path: string, body?: TRequestData, config?: AxiosRequestConfig): Promise<T> {
    const headers = this.buildHeaders();
    return this.request<T>("put", path, body, {
      ...config,
      headers: { ...headers, ...config?.headers },
    });
  }

  async get<T = never>(path: string, config?: AxiosRequestConfig): Promise<T> {
    const headers = this.buildHeaders();
    return this.request<T>("get", path, undefined, {
      ...config,
      headers: { ...headers, ...config?.headers },
    });
  }

  async getWithResponse<T = never>(
    path: string,
    config?: AxiosRequestConfig
  ): Promise<{ data: T; status: number }> {
    const headers = this.buildHeaders();
    const response = await this.axiosInstance.request<T>({
      method: "get",
      url: path,
      headers: { ...headers, ...config?.headers },
      ...config,
    });
    return { data: response.data, status: response.status };
  }

  async delete<T = never>(path: string, config?: AxiosRequestConfig): Promise<T> {
    const headers = this.buildHeaders();
    return this.request<T>("delete", path, undefined, {
      ...config,
      headers: { ...headers, ...config?.headers },
    });
  }
}

const apiBaseUrl = import.meta.env.VITE_API_URL || "https://api-qas.talk2buy.com.br";
const httpRequest = new HttpClient(apiBaseUrl);
export default httpRequest;
